/**
 * Pure helpers for the 3D Secure (Stripe SCA) booking challenge. No React, no
 * Stripe instance — error parsing, user-facing copy, and a typed error so the
 * booking hook and UI stay simple and testable. See
 * docs/superpowers/specs/2026-07-27-3dsecure-web-design.md.
 */

/** Copy shown when the bank authentication fails, errors, or is not completed. */
export const THREE_DS_FAILED_MESSAGE =
  "We couldn't verify your card. Please try again or use a different payment method."

/**
 * Copy shown when the issuer declined the card. Distinct from
 * THREE_DS_FAILED_MESSAGE because the card *was* verified — the bank refused the
 * charge — so "we couldn't verify your card" misdescribes what happened. Retrying
 * the same card rarely helps a decline (do_not_honor and insufficient_funds both
 * persist), so the copy steers to a different card or the issuer instead.
 *
 * Deliberately generic: Stripe advises against surfacing the specific
 * `decline_code` to shoppers, since issuers reuse codes loosely and the detail can
 * be both wrong and sensitive. The code is still captured in analytics.
 */
export const THREE_DS_DECLINED_MESSAGE =
  'Your card was declined. Please use a different payment method, or contact your bank if the problem continues.'

/**
 * Extracts the 3D Secure challenge details from a booking error, if present.
 *
 * The Starbase booking endpoint returns HTTP 400 with a validation error whose
 * items are preserved on `err.errors` (see bookAppointment / formatAndRethrowError).
 * A challenge item has `type === '3dSecureRequired'` and carries the PaymentIntent
 * id + client secret needed to run the Stripe challenge.
 *
 * @param {*} err - the error thrown by bookAppointment.
 * @returns {{paymentIntentId: string, paymentIntentClientSecret: string} | null}
 *   the challenge details, or null when this is not a 3DS challenge.
 */
export const parse3DSError = err => {
  const item =
    err && Array.isArray(err.errors)
      ? err.errors.find(e => e && e.type === '3dSecureRequired')
      : undefined
  if (item && item.paymentIntentId && item.paymentIntentClientSecret) {
    return {
      paymentIntentId: item.paymentIntentId,
      paymentIntentClientSecret: item.paymentIntentClientSecret,
    }
  }
  return null
}

/**
 * Whether stripe.handleNextAction authenticated the PaymentIntent.
 *
 * Web has no reliable user-cancel signal — a cancelled and a genuinely failed
 * challenge both come back as a `payment_intent_authentication_failure` error —
 * so both are treated as failures. Only an advanced PaymentIntent status (the
 * challenge completed) authorizes the booking retry; an error, or a PaymentIntent
 * still requiring action, is a failure and must not retry.
 *
 * @param {{error?: object, paymentIntent?: {status?: string}}} [result]
 * @returns {boolean}
 */
export const is3DSAuthenticated = ({ error, paymentIntent } = {}) => {
  if (error) {
    return false
  }
  const status = paymentIntent && paymentIntent.status
  return !!status && status !== 'requires_action' && status !== 'requires_payment_method'
}

/** Stripe error code for a 3D Secure challenge that did not authenticate. */
export const THREE_DS_AUTHENTICATION_FAILURE_CODE = 'payment_intent_authentication_failure'

/**
 * Classifies the result of `stripe.handleNextAction` into the outcome reported to
 * analytics.
 *
 * A presented challenge can end three ways, and the first version of this code
 * reported the last two identically as 'failed'. That made the Booking Challenge
 * funnel read as a 3D Secure failure rate when it was mostly an issuer decline
 * rate — of the first twelve challenges in production, nine were plain card
 * declines that never reached an authentication step:
 *
 * - `completed` - the PaymentIntent advanced past the challenge.
 * - `declined`  - the card itself was refused (a Stripe `card_error`, most often
 *                 `card_declined` carrying a `decline_code` such as `do_not_honor`
 *                 or `insufficient_funds`). Authentication is not implicated; the
 *                 payment would have failed with or without 3D Secure, so these
 *                 must be excluded from any 3DS health metric.
 * - `failed`    - authentication genuinely did not succeed.
 *
 * A PaymentIntent left at `requires_payment_method` with no error is ambiguous —
 * it is the resting status after both an authentication failure and a decline —
 * so it stays classified as `failed` rather than guessing.
 *
 * @param {{error?: {code?: string, type?: string, decline_code?: string}, paymentIntent?: {status?: string}}} [result]
 * @returns {{outcome: 'completed'|'declined'|'failed', reason?: string, declineCode?: string}}
 */
export const classify3DSOutcome = (result = {}) => {
  const { error } = result

  // Checked ahead of the card_error branch below. An authentication failure is
  // never a decline, whatever `type` Stripe attaches to it — every such error
  // seen in production carries `invalid_request_error`, but keying off the code
  // keeps this correct even if that changes, and stops the broad card_error
  // catch-all from swallowing the one outcome this metric exists to measure.
  if (error?.code === THREE_DS_AUTHENTICATION_FAILURE_CODE) {
    return { outcome: 'failed', reason: THREE_DS_AUTHENTICATION_FAILURE_CODE }
  }

  if (error && (error.code === 'card_declined' || error.type === 'card_error')) {
    return {
      outcome: 'declined',
      reason: error.code || 'card_declined',
      declineCode: error.decline_code,
    }
  }

  if (is3DSAuthenticated(result)) {
    return { outcome: 'completed' }
  }

  return {
    outcome: 'failed',
    reason: error?.code || result?.paymentIntent?.status || 'authentication_failed',
  }
}

/**
 * Error thrown when a 3D Secure challenge fails, is declined, or is not completed.
 * Carries the `reason` (Stripe error code or PaymentIntent status) and, for issuer
 * declines, the `declineCode` for analytics.
 */
export class ThreeDSError extends Error {
  /**
   * @param {string} message - user-facing message.
   * @param {string} [reason] - the Stripe error code or PaymentIntent status.
   * @param {string} [declineCode] - Stripe `decline_code` when the issuer declined.
   */
  constructor(message, reason = 'Unknown', declineCode = undefined) {
    super(message)
    this.name = 'ThreeDSError'
    this.reason = reason
    this.declineCode = declineCode
  }
}
